import { isFinite } from 'lodash';
import { LngLatLike } from 'mapbox-gl';
import { useEffect, useState } from 'react';

import { useDevice } from '@/api/devices';
import { MapBaseLayerToggle, MapControlsStack } from '@/components/map/LayerControls';
import { SafeMap } from '@/components/map/SafeMap';
import { useMapUiContext } from '@/context/MapUiContext';
import { useTetherDeviceIdentity } from '@/hooks/useTetherDeviceIdentity';
import { spotterToLngLat } from '@/utils';

import { DEFAULT_GEOLOCATION, ScaleControl } from './SpotterMap';
import { GeofenceLayer, GeofenceLayerProps } from './layers/GeofenceLayer';
import { SpotterTrackLayer } from './layers/SpotterTrackLayer';

export const SpotterGeofenceMap = (props: GeofenceLayerProps) => {
    const { center } = props;
    const { weatherLayers } = useMapUiContext();
    const [initialLocation, setInitialLocation] = useState<LngLatLike>();

    const { data: deviceResult, isPending: isDevicePending } = useDevice();
    const { isScout } = useTetherDeviceIdentity();
    // Spotters are initialized in wavefleet with null lat/lon in geofence. So it's possible that the center coordinates are missing.
    const hasGeofenceCoordinates = isFinite(center?.lat) && isFinite(center?.lng);

    useEffect(() => {
        // Set initial map center
        if (initialLocation || isDevicePending || !deviceResult) {
            return;
        }

        if (deviceResult?.track?.[0]) {
            setInitialLocation(spotterToLngLat(deviceResult.track[0]));
        } else if (hasGeofenceCoordinates) {
            setInitialLocation(center);
        } else {
            setInitialLocation(spotterToLngLat(DEFAULT_GEOLOCATION));
        }
    }, [isDevicePending, deviceResult, initialLocation, center, hasGeofenceCoordinates]);

    return (
        <SafeMap
            mapType="geofence"
            showWeather={false}
            allowMapZoomOnScroll
            weatherLayers={weatherLayers}
            center={initialLocation}
            zoom={12}
        >
            {deviceResult && (
                <SpotterTrackLayer
                    track={deviceResult.track}
                    selectedLocation={deviceResult.track?.[0]}
                    isScout={isScout}
                />
            )}
            {deviceResult && hasGeofenceCoordinates && (
                <GeofenceLayer {...props} spotterLocation={deviceResult.track[0]} />
            )}
            <ScaleControl />
            <MapControlsStack sx={{ right: '4px', bottom: '4px' }}>
                <MapBaseLayerToggle size="1.5em" sx={{ p: '2px' }} />
            </MapControlsStack>
        </SafeMap>
    );
};
